import React from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { useCurrency } from "../../context/CurrencyContext";

const NAMES = {
  NGN: "Nigerian Naira",
  GHS: "Ghanaian Cedi",
  ZAR: "South African Rand",
  KES: "Kenyan Shilling",
  USD: "US Dollar",
};

// Reusable "Pay with Paystack" control. `initialize(currency)` must return an authorization_url.
export default function PaystackButton({ initialize, disabled = false, variant = "dark" }) {
  const { currency: globalCurrency } = useCurrency();
  const [config, setConfig] = React.useState(null);
  const [cur, setCur] = React.useState("NGN");
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    api.get("/paystack/config")
      .then(({ data }) => {
        setConfig(data);
        if (data?.currencies?.includes(globalCurrency)) setCur(globalCurrency);
      })
      .catch(() => setConfig({ configured: false }));
  }, [globalCurrency]);

  if (!config || !config.configured) return null;
  const currencies = config.currencies || ["NGN"];
  const dark = variant === "dark";

  const pay = async () => {
    setBusy(true);
    try {
      const url = await initialize(cur);
      if (url) {
        window.location.href = url;
      } else {
        setBusy(false);
        toast.error("Could not start Paystack payment.");
      }
    } catch (e) {
      setBusy(false);
      const detail = e?.response?.data?.detail;
      if (detail) toast.error(detail);
    }
  };

  return (
    <div data-testid="paystack-section" className="mt-3">
      <div className={`text-center text-xs my-3 ${dark ? "text-cream/40" : "text-ink-muted"}`}>or</div>
      <div className="flex items-center gap-2 mb-2">
        <label className={`text-xs whitespace-nowrap ${dark ? "text-cream/60" : "text-ink-muted"}`}>Pay in</label>
        <select
          value={cur}
          onChange={(e) => setCur(e.target.value)}
          data-testid="paystack-currency-select"
          className={`flex-1 rounded-lg px-3 py-2 text-sm outline-none ${
            dark ? "bg-night border border-cream/20 text-cream focus:border-gold" : "bg-white border border-parchment text-ink focus:border-gold"
          }`}
        >
          {currencies.map((c) => (
            <option key={c} value={c}>{c} — {NAMES[c] || c}</option>
          ))}
        </select>
      </div>
      <button
        onClick={pay}
        disabled={disabled || busy}
        data-testid="paystack-button"
        className="w-full inline-flex items-center justify-center gap-2 bg-[#09A5DB] text-white font-semibold py-3 rounded-full hover:bg-[#0790c4] transition-colors disabled:opacity-60"
      >
        {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Redirecting…</> : "Pay with Paystack"}
      </button>
      <p className={`text-[11px] mt-1.5 text-center ${dark ? "text-cream/40" : "text-ink-muted"}`}>
        Card, bank &amp; mobile money · secured by Paystack
      </p>
    </div>
  );
}
